const DEMAT_REGEX = /^(IN\d{14}|\d{16})$/i;

function isPresentValue(val) {
  if (val == null) return false;
  const s = String(val).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== 'null' && lower !== 'undefined';
}

export function normalizeDematForMatch(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function dematAccountsMatch(dematA, dematB) {
  const a = normalizeDematForMatch(dematA);
  const b = normalizeDematForMatch(dematB);
  if (!a || !b) return false;
  if (a === b) return true;
  const digitsA = a.replace(/\D/g, '');
  const digitsB = b.replace(/\D/g, '');
  if (!digitsA || !digitsB) return false;
  if (digitsA === digitsB) return true;
  if (digitsA.length >= 8 && digitsB.length >= 8) {
    return digitsA.endsWith(digitsB) || digitsB.endsWith(digitsA);
  }
  return false;
}

export function extractDematFromStatus(record) {
  const raw = String(
    record?.demat_account_no || record?.demat_number || record?.demat_account_number || ''
  ).trim().toUpperCase();
  return {
    demat_account_no: raw,
    demat_account_id: record?.demat_account_id != null ? String(record.demat_account_id) : '',
    dp_name: String(record?.dp_name || '').trim(),
    status: String(record?.status || '').trim(),
    cmr_document_url: String(record?.cmr_document_url || '').trim(),
  };
}

export function extractDematFromCmr(extraction) {
  const raw = String(extraction?.demat_account || '').trim().toUpperCase();
  return {
    demat_account_no: raw,
    demat_account_id: '',
    dp_name: '',
    status: '',
    cmr_document_url: '',
  };
}

export function hasUsableCmrDemat(fields) {
  const val = String(fields?.demat_account_no || '').trim().toUpperCase().replace(/\s/g, '');
  return DEMAT_REGEX.test(val);
}

export function normalizeExistingDematRecord(record, index = 0) {
  const raw = record && typeof record === 'object' ? record : {};
  const fields = extractDematFromStatus(raw);
  const normalized = normalizeDematForMatch(fields.demat_account_no);
  const apiId = String(raw.demat_account_id ?? raw.id ?? '').trim();
  const optionKey = apiId || `demat-${index}-${normalized || 'na'}`;
  return {
    ...raw,
    optionKey,
    demat_account_id: apiId || optionKey,
    demat_account_no: fields.demat_account_no || raw.demat_account_no || '',
    dp_name: fields.dp_name,
    status: fields.status,
    cmr_document_url: fields.cmr_document_url,
  };
}

export function normalizeExistingDemats(records) {
  const list = Array.isArray(records) ? records : [];
  const seen = new Set();
  return list.map((record, index) => {
    let normalized = normalizeExistingDematRecord(record, index);
    if (seen.has(normalized.optionKey)) {
      normalized = { ...normalized, optionKey: `${normalized.optionKey}-${index}` };
    }
    seen.add(normalized.optionKey);
    return normalized;
  });
}

export function getDematOptionValue(demat) {
  const key = String(demat?.optionKey || demat?.demat_account_id || '').trim();
  return `saved:${key}`;
}

export function parseDematOptionKey(value) {
  if (!value || !String(value).startsWith('saved:')) return null;
  return String(value).slice('saved:'.length);
}

/**
 * Returns true when the system cannot automatically pick which demat to use.
 * Returns false when session demat is already usable (back-navigation).
 */
export function needsDematSourceResolution(existingDemats, cmrDematFields, sessionDemat = null) {
  if (sessionDemat && hasUsableCmrDemat(extractDematFromStatus(sessionDemat))) {
    return false;
  }
  const demats = Array.isArray(existingDemats) ? existingDemats : [];
  const count = demats.length;
  const hasCmr = hasUsableCmrDemat(cmrDematFields);
  if (count >= 2) return true;
  if (count === 1 && hasCmr) {
    return !dematAccountsMatch(demats[0]?.demat_account_no, cmrDematFields?.demat_account_no);
  }
  return false;
}
